import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import {
  Card,
  CardSection,
  SmallHeader,
  Gutter,
  CondensedLarge,
  CondensedHeader,
  CardHeader,
  CandidateDescription,
  TalentExperiences,
  TalentExperiencesHighlightedItem,
  TalentEducation,
  TalentCertifications,
  TalentQualifications,
  TalentLanguages,
  useApi,
  CandidateHighlightExperienceSummary,
  CandidateHighlightExperienceDetails,
  ToastUtil,
  CandidateHighlightsUtil,
  CalendarEventsUtil,
  CalendarPermissionsUtil,
  CalendarTimeZoneUtil,
  LegalTechSkillsDisplay,
  Flashy,
  Icon,
  CardFooter,
  ParagraphHeader,
  Tiles,
  TilesItem,
  DateUtil,
  useBreakpoint,
  BadRequestError,
  SectionHeader,
  GroupedExperiencesUtil,
} from '@axiom/ui';
import {
  AccountConst,
  PermissionImpersonationRoles,
  Certification as CertificationConst,
  CandidateOpportunitiesConst,
} from '@axiom/const';
import type {
  AfcSubmissionCandidateOpportunity,
  CalendarEventType,
  CalendarManualInterview,
  CalendarScheduledInterview,
} from '@axiom/validation';
import { useNavigate, Link } from 'react-router';
import { SubmissionCandidateModel } from '@axiom/ui-models';

import { AccountSubmissionsLegacyApi } from '../../api/account-submissions-legacy-api';
import { AccountLegacyApi } from '../../api/account-legacy-api';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { CalendarApi } from '../../api/calendar-api';
import { PublicEngagementToLoginModal } from '../PublicEngagementToLoginModal/PublicEngagementToLoginModal';
import { CandidateCardHeader } from '../CandidateCardHeader/CandidateCardHeader';
import { EngagementPrompt } from '../EngagementPrompt/EngagementPrompt';
import { InstantMatchesApi } from '../../api/instant-matches-api';
import type { EngagementPassModalForm } from '../EngagementPassModal/EngagementPassModal';
import { EngagementPassModal } from '../EngagementPassModal/EngagementPassModal';
import { TalentInterviewScheduleFlow } from '../TalentInterviewScheduleFlow/TalentInterviewScheduleFlow';
import { TalentInterviewConfirmModal } from '../TalentInterviewConfirmModal/TalentInterviewConfirmModal';
import type { TalentInterviewConfirmModalType } from '../../schema/talent-interview-confirm-modal-schema';
import { useEngagementTalentData } from '../../hooks/useEngagementTalentData';
import { CandidateReputation } from '../CandidateReputation/CandidateReputation';
import { TalentInterviewPromptModal } from '../TalentInterviewPromptModal/TalentInterviewPromptModal';
import { ClientRequestApi } from '../../api/client-request-api';
import { SubmissionApi } from '../../api/submission-api';
import type { ClientTogglePinStatus } from '../../schema/client-toggle-pin-status';
import { TalentInterviewLegalTeamQuestionsModal } from '../TalentInterviewLegalTeamQuestionsModal/TalentInterviewLegalTeamQuestionsModal';
import type { TalentInterviewLegalTeamQuestionsType } from '../../schema/talent-interview-legal-team-questions-modal-schema';
import { AccountIntakeApi } from '../../api/account-intake-api';
import { SubmissionsUtil } from '../../utils/submissions-util';

import { EngagementTalentMatchChecklist } from './EngagementTalentMatchChecklist';

const showRequestMessage = (
  candidateStatus: AfcSubmissionCandidateOpportunity['candidateStatus']
) => {
  switch (candidateStatus) {
    case CandidateOpportunitiesConst.CandidateStatuses.Selected: {
      return (
        <CardFooter stackableOn="mobile" name="CTA_FOOTER">
          <CondensedLarge name="PENDO_REQUESTED_HIRE">
            <Gutter right="8px" as="span">
              Engagement Requested
            </Gutter>
            <Flashy color="dataVizGreen">
              <Icon name="circle-check-filled" />
            </Flashy>
          </CondensedLarge>
        </CardFooter>
      );
    }
    default: {
      return null;
    }
  }
};

const { QUALIFICATION_AWARDS } = CertificationConst;

export const EngagementTalentDetails = () => {
  const navigate = useNavigate();
  const [displayLoginModal, setDisplayLoginModal] = useState(false);

  const {
    accountId,
    candidateOpp,
    allCandidateOpps,
    submission,
    talentSlug,
    groupedExperiences,
    legalTechSkills,
    user,
  } = useEngagementTalentData();

  const { highlightedExperiences, regularGroups } = useMemo(
    () => GroupedExperiencesUtil.splitHighlighted(groupedExperiences),
    [groupedExperiences]
  );

  const { isMobile } = useBreakpoint();
  const [showPassModal, setShowPassModal] = useState(false);
  const [showEngageConfirmPrompt, setShowEngageConfirmPrompt] = useState(false);
  const [showInterviewAuto, setShowInterviewAuto] = useState(false);
  const [showInterviewConfirmModal, setShowInterviewConfirmModal] =
    useState(false);
  const [showInterviewPromptModal, setShowInterviewPromptModal] =
    useState(false);
  const [
    showInterviewLegalTeamQuestionsModal,
    setShowInterviewLegalTeamQuestionsModal,
  ] = useState(false);

  const [legalTeamData, setLegalTeamData] =
    useState<Partial<TalentInterviewLegalTeamQuestionsType>>();

  const [formattedPrompt, setFormattedPrompt] = useState('');
  const [tempEvent, setTempEvent] = useState<CalendarEventType>();
  const [{ data: calendarData } = { data: undefined }] = useApi(
    candidateOpp?.candidate.calendar?.id
      ? CalendarApi.readCalendar(candidateOpp.candidate.calendar.id)
      : undefined
  );

  if (!submission) {
    throw new Error('Submission not found');
  }
  if (!candidateOpp) {
    throw new Error('Candidate Opportunity not found');
  }

  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());

  const maxNumberOfInterviewPills = 5;

  const awardTypes = Object.values(QUALIFICATION_AWARDS);
  const certifications = candidateOpp.candidate.certifications ?? [];
  const hasCertifications = certifications.some(
    cert => !awardTypes.includes(cert.award)
  );
  const hasQualifications = certifications.some(cert =>
    awardTypes.includes(cert.award)
  );

  const handleDataRefresh = async () => {
    const refreshCalls = [
      accountId &&
        AccountLegacyApi.refreshAccount(accountId, {
          expand: [AccountConst.Expansions.accountTeamUserInfo],
        }),
      SubmissionApi.refreshSubmissionDetails(submission.submissionId),
      AccountSubmissionsLegacyApi.refreshCandidateAccountSubmissionDetails(
        accountId ?? '',
        submission.submissionId,
        candidateOpp.candidate.id
      ),
      InstantMatchesApi.refreshInstantMatches(submission.submissionId),
      candidateOpp.candidate.calendar?.id &&
        CalendarApi.refreshCalendar(candidateOpp.candidate.calendar.id),
      accountId && AccountIntakeApi.refreshAccountIntake(accountId),
    ].filter((call): call is Promise<unknown> => Boolean(call));
    return Promise.all(refreshCalls);
  };

  const handlePinToggle = async () => {
    if (!user) {
      // If no user this must be the public view; send user to login
      setDisplayLoginModal(true);
      return;
    }
    const body: ClientTogglePinStatus = {
      candidateId: candidateOpp.candidate.id,
      opportunityId: submission.id,
      isPinnedByClient: !candidateOpp.pinnedByClient,
    };
    await ClientRequestApi.updatePinnedStatus(body);
    await handleDataRefresh();
  };

  const handlePassClick = () => {
    if (!user) {
      // If no user this must be the public view; send user to login
      setDisplayLoginModal(true);
      return;
    }

    setShowPassModal(!showPassModal);
  };

  const handleClosePassModal = () => {
    setShowPassModal(!showPassModal);
  };

  const handleAfterPassedOrInterviewNavigation = () => {
    const allCandidateIds = allCandidateOpps.map(
      (item: AfcSubmissionCandidateOpportunity) => item.candidate.id
    );

    const talentIndex = allCandidateIds.indexOf(candidateOpp.candidate.id);
    const nonPassedTalent = [...allCandidateIds];
    nonPassedTalent.splice(talentIndex, 1);

    let nextTalent: string | null | undefined = null;

    if (nonPassedTalent.length > 0) {
      nextTalent =
        talentIndex === allCandidateIds.length - 1
          ? allCandidateIds[talentIndex - 1]
          : allCandidateIds[talentIndex + 1];
    }

    if (nextTalent) {
      const to =
        talentIndex === 0 && !talentSlug
          ? `./${nextTalent}`
          : `../${nextTalent}`;
      void navigate(to);
    }
  };

  const handleSubmitPassModal = async (formData: EngagementPassModalForm) => {
    handleClosePassModal();
    await (candidateOpp.isInstantMatch
      ? InstantMatchesApi.updateInstantMatchPassed({
          candidateId: candidateOpp.candidate.id,
          opportunityId: submission.id,
        })
      : ClientRequestApi.createPassOnCandidateRequest({
          submissionId: submission.submissionId,
          candidateId: candidateOpp.candidate.id,
          rejectionLossCodes: [formData.rejectionLossCode],
          notes: formData.notes,
        }));
    await handleDataRefresh();
    handleAfterPassedOrInterviewNavigation();
  };

  const handleInterviewRequestClick = () => {
    if (!user) {
      // If no user this must be the public view; send user to login
      setDisplayLoginModal(true);
      return;
    }

    const calendarPermissions = CalendarPermissionsUtil(
      user,
      PermissionImpersonationRoles.client
    ).userPermissions();

    const hasFreeTime = CalendarEventsUtil.hasAvailableFreeTimeSlots(
      calendarData?.events,
      calendarPermissions
    );

    if (hasFreeTime && !isMobile) {
      setShowInterviewAuto(true);
    } else {
      setShowInterviewPromptModal(true);
    }
  };

  const loadToast = () => {
    ToastUtil.add({
      name: `INTERVIEW_REQUESTED`,
      type: 'info',
      children: (
        <>
          <CondensedHeader>Interview Requested</CondensedHeader>
          <CondensedLarge>
            We have added this match to the{' '}
            <Link
              data-test="TO_INTERVIEW_TAB"
              to={`${window.location.pathname.split('matches')[0]}interviews`}
            >
              Interviews tab
            </Link>{' '}
            and will confirm with everyone when the interview is set up.
          </CondensedLarge>
        </>
      ),
    });
  };

  const loadFailureToast = () => {
    ToastUtil.add({
      name: `FAILED_INTERVIEW_OR_ENGAGE_REQUEST`,
      type: 'error',
      dismissible: true,
      children: (
        <>
          <CondensedHeader>Error - Please refresh your browser</CondensedHeader>
          <CondensedLarge>
            Looks like there was an issue updating some of the data on this
            page.{' '}
            <Link
              data-test="REFRESH_LINK"
              to="#"
              onClick={e => {
                e.preventDefault();
                SubmissionApi.refreshSubmissionDetails(submission.submissionId)
                  .then(() => {
                    ToastUtil.removeAll();
                  })
                  .catch(() => {});
              }}
            >
              Refresh
            </Link>
          </CondensedLarge>
        </>
      ),
    });
  };

  const handleEngageRequestToggle = () => {
    if (!user) {
      // If no user this must be the public view; send user to login
      setDisplayLoginModal(true);
      return;
    }

    setShowEngageConfirmPrompt(!showEngageConfirmPrompt);
  };

  const handleEngageConfirm = async () => {
    setShowEngageConfirmPrompt(false);
    try {
      await ClientRequestApi.createEngagementRequest({
        submissionId: submission.submissionId,
        candidateId: candidateOpp.candidate.id,
      });
      await handleDataRefresh();
    } catch {
      loadFailureToast();
    }
  };

  const handleLegalTeamDataUpdated = async (
    data?: Partial<TalentInterviewLegalTeamQuestionsType>
  ) => {
    if (data && accountId) {
      await AccountIntakeApi.updateAccountIntake(
        accountId,
        data,
        submission.submissionId
          ? {
              submissionId: submission.submissionId,
            }
          : undefined
      );
    }
  };

  const buildBaseInterviewPayload = (
    data?: TalentInterviewConfirmModalType
  ) => ({
    adhocAttendees: data?.adHocContacts,
    contactIds: data?.contacts,
    preferredVideoClient: data?.isZoomNotPreferred
      ? null
      : AccountConst.PreferredVideoClient.zoom,
    candidateId: candidateOpp.candidate.id,
    submissionId: submission.submissionId,
  });

  const handleInterviewError = (error: unknown) => {
    if (error instanceof BadRequestError) {
      const errorObj = error.getResponse();
      if (errorObj?.text?.includes('domain')) {
        throw error;
      }
    }
    loadFailureToast();
    setShowInterviewAuto(false);
  };

  const confirmInterview = async (data?: TalentInterviewConfirmModalType) => {
    const payload = {
      ...buildBaseInterviewPayload(data),
      availabilityNote: data?.availabilityNote,
    } as CalendarManualInterview;

    try {
      await CalendarApi.createManualInterview(payload);
      await handleDataRefresh();
      setShowInterviewConfirmModal(false);

      loadToast();
      setShowInterviewAuto(false);
    } catch (error) {
      handleInterviewError(error);
    }
  };

  const scheduleInterview = async (
    data?: TalentInterviewConfirmModalType,
    scheduledEvent?: CalendarEventType
  ) => {
    const payload = {
      ...buildBaseInterviewPayload(data),
      title: scheduledEvent?.title,
      startTime: scheduledEvent?.startTime,
      endTime: scheduledEvent?.endTime,
      busy: scheduledEvent?.busy,
    } as CalendarScheduledInterview;

    try {
      await CalendarApi.createScheduledCalendarInterview(payload);

      await handleDataRefresh();
      loadToast();
      setShowInterviewAuto(false);
    } catch (error) {
      handleInterviewError(error);
    }
  };

  const candidateModel = new SubmissionCandidateModel(candidateOpp);
  const candidateDescription = candidateModel.calculatedDescription();

  const primaryContactId = SubmissionsUtil.getPrimaryOwnerId(submission);
  const futureAvailabilityEvents = (calendarData?.events ?? [])
    .filter(
      event =>
        !DateUtil.isDateBeforeDate(event.startTime, new Date(), true) &&
        !event.busy
    )
    .sort((a, b) => {
      const dateA = new Date(a.startTime).getTime();
      const dateB = new Date(b.startTime).getTime();
      return dateA - dateB;
    });
  const interviewTileAllowList = ['Interviewing', 'Submitted', null, undefined];
  return (
    <div data-test="talent-details">
      <Card>
        <CardHeader>
          <CandidateCardHeader
            showContextMenu
            candidateOpp={candidateOpp}
            onInterview={handleInterviewRequestClick}
            submission={submission}
            onPass={handlePassClick}
            onEngage={handleEngageRequestToggle}
            updatePinnedStatus={handlePinToggle}
          />
        </CardHeader>
        {user &&
        submission.isQualificationComplete &&
        interviewTileAllowList.includes(candidateOpp.candidateStatus) &&
        futureAvailabilityEvents.length > 0 ? (
          <CardSection name="INTERVIEW_AVAILABILITY_PILL_SECTION">
            <ParagraphHeader name="EARLIEST_INTERVIEW_AVAILABILITY_HEADER">
              Earliest Interview Availability
            </ParagraphHeader>
            <Gutter bottom="8px" />
            <Tiles>
              {futureAvailabilityEvents
                .slice(0, maxNumberOfInterviewPills)
                .map(event => {
                  const browserIana = moment.tz.guess();
                  const fancyTimeRange = DateUtil.displayFancyTimeRange(
                    event.startTime,
                    event.endTime,
                    browserIana
                  );
                  const fancyTimeRangeWithTimezone = fancyTimeRange
                    ? `${fancyTimeRange} ${
                        CalendarTimeZoneUtil.getZoneFromMomentZone(
                          moment.tz(new Date(), browserIana)
                        )?.abbr ?? ''
                      }`
                    : null;
                  return (
                    <TilesItem
                      name="INTERVIEW_AVAILABILITY_PILL"
                      key={event.id}
                      onClick={() => {
                        if (isMobile) {
                          setShowInterviewPromptModal(true);
                        } else {
                          setTempEvent(event);
                          setShowInterviewAuto(true);
                        }
                      }}
                    >
                      {fancyTimeRangeWithTimezone}
                    </TilesItem>
                  );
                })}
            </Tiles>
          </CardSection>
        ) : null}
        {!!candidateDescription && (
          <CardSection divider name="DESCRIPTION_SECTION">
            <CandidateDescription
              description={candidateDescription}
              calculatedFirstName={candidateOpp.candidate.calculatedFirstName}
              formatting={candidateModel.calculatedDescriptionFormatting()}
            />
            {submission.selfService ? (
              <EngagementTalentMatchChecklist
                candidate={candidateOpp.candidate}
                legalTechSkills={legalTechSkills}
                submission={submission}
                taxonomy={taxonomy}
              />
            ) : null}
          </CardSection>
        )}

        {showRequestMessage(candidateOpp.candidateStatus)}
      </Card>
      <Gutter bottom="16px" />
      {CandidateHighlightsUtil.calculateNumberOfHighlights(
        candidateOpp.candidate,
        submission
      ) > 0 && (
        <Card name="HIGHLIGHTS_CARD">
          <CardHeader>
            <SmallHeader name="HIGHLIGHTS_HEADER">Highlights</SmallHeader>
          </CardHeader>
          <CardSection>
            <CandidateHighlightExperienceSummary
              candidate={candidateOpp.candidate}
              submission={submission}
              taxonomy={taxonomy}
            />
            <Gutter top="16px">
              <CandidateHighlightExperienceDetails
                candidate={candidateOpp.candidate}
                taxonomy={taxonomy}
              />
            </Gutter>
          </CardSection>
        </Card>
      )}
      <Gutter bottom="16px" />
      <CandidateReputation candidateOpp={candidateOpp} />
      <Gutter bottom="16px" />
      {groupedExperiences.length > 0 && (
        <Gutter bottom="16px">
          <Card name="EXPERIENCE_CARD">
            <CardHeader>
              <SmallHeader>Experience</SmallHeader>
            </CardHeader>
            {highlightedExperiences.length > 0 ? (
              <CardSection name="HIGHLIGHTED_EXPERIENCES_SECTION" divider>
                <SectionHeader name="HIGHLIGHTED_EXPERIENCES_HEADER">
                  Highlighted Experiences
                </SectionHeader>
                <Gutter top="16px">
                  {highlightedExperiences.map((exp, i) => (
                    <Gutter key={exp.id} bottom="16px">
                      <TalentExperiencesHighlightedItem
                        name={`HIGHLIGHTED_EXPERIENCE_ITEM_${i}`}
                        experience={exp}
                      />
                    </Gutter>
                  ))}
                </Gutter>
              </CardSection>
            ) : null}
            <TalentExperiences
              name="TALENT_EXPERIENCES"
              groupedExperiences={regularGroups}
            />
          </Card>
        </Gutter>
      )}
      {legalTechSkills.length > 0 && (
        <Gutter bottom="16px">
          <Card name="LEGAL_TECH_SKILLS_CARD">
            <CardHeader>
              <SmallHeader name="LEGAL_TECH_SKILLS_HEADER">
                Legal Tech Skills
              </SmallHeader>
            </CardHeader>
            <CardSection>
              <LegalTechSkillsDisplay
                legalTechSkills={legalTechSkills}
                taxonomy={taxonomy}
              />
            </CardSection>
          </Card>
        </Gutter>
      )}
      {(candidateOpp.candidate.degrees?.length ?? 0) > 0 && (
        <Gutter bottom="16px">
          <Card name="EDUCATION_CARD">
            <CardHeader>
              <SmallHeader name="EDUCATION_HEADER">Education</SmallHeader>
            </CardHeader>
            <CardSection>
              <TalentEducation candidate={candidateOpp.candidate} />
            </CardSection>
          </Card>
        </Gutter>
      )}
      {(candidateOpp.candidate.certifications?.length ?? 0) > 0 && (
        <Gutter bottom="16px">
          <Card name="certifications">
            <CardHeader>
              <SmallHeader name="ACCOMPLISHMENTS_HEADER">
                Accomplishments
              </SmallHeader>
            </CardHeader>
            {hasCertifications ? (
              <CardSection divider name="certifications-subsection">
                <TalentCertifications candidate={candidateOpp.candidate} />
              </CardSection>
            ) : null}
            {hasQualifications ? (
              <CardSection name="qualifications-subsection">
                <TalentQualifications candidate={candidateOpp.candidate} />
              </CardSection>
            ) : null}
          </Card>
        </Gutter>
      )}
      {(candidateOpp.candidate.languages?.length ?? 0) > 0 && (
        <Gutter bottom="16px">
          <Card name="LANGUAGES_CARD">
            <CardHeader>
              <SmallHeader name="LANGUAGES_HEADER">Languages</SmallHeader>
            </CardHeader>
            <CardSection>
              <TalentLanguages candidate={candidateOpp.candidate} />
            </CardSection>
          </Card>
        </Gutter>
      )}

      {!!showPassModal && (
        <EngagementPassModal
          candidate={candidateOpp.candidate}
          closeModal={handleClosePassModal}
          submitModal={handleSubmitPassModal}
        />
      )}

      {!!showInterviewPromptModal && (
        <TalentInterviewPromptModal
          candidate={candidateOpp.candidate}
          onClose={() => {
            setShowInterviewPromptModal(false);
          }}
          onContinue={(timezoneId, description) => {
            const t = CalendarTimeZoneUtil.getZoneFromId(timezoneId);
            setFormattedPrompt(`${t.abbr} - ${t.name}<br>${description}`);
            setShowInterviewPromptModal(false);
            if (submission.selfService && accountId) {
              setShowInterviewLegalTeamQuestionsModal(true);
            } else {
              setShowInterviewConfirmModal(true);
            }
          }}
        />
      )}

      {!!showInterviewLegalTeamQuestionsModal && accountId ? (
        <TalentInterviewLegalTeamQuestionsModal
          accountId={accountId}
          candidate={candidateOpp.candidate}
          onClose={() => {
            setShowInterviewLegalTeamQuestionsModal(false);
          }}
          onContinue={data => {
            setLegalTeamData(data);
            setShowInterviewLegalTeamQuestionsModal(false);
            setShowInterviewConfirmModal(true);
          }}
          submissionId={submission.submissionId}
        />
      ) : null}

      {!!showInterviewConfirmModal && user ? (
        <TalentInterviewConfirmModal
          loggedInUser={user}
          submission={submission}
          candidate={candidateOpp.candidate}
          onClose={() => {
            if (legalTeamData) {
              setLegalTeamData(undefined);
            }
            setShowInterviewConfirmModal(false);
          }}
          onConfirm={async confirmData => {
            if (legalTeamData) {
              await handleLegalTeamDataUpdated(legalTeamData);
            }
            await confirmInterview(confirmData);
          }}
          primaryContactId={primaryContactId}
          availabilityNote={formattedPrompt}
        />
      ) : null}

      {showInterviewAuto && user && calendarData ? (
        <TalentInterviewScheduleFlow
          accountId={accountId}
          tempEvent={tempEvent}
          user={user}
          submission={submission}
          candidate={candidateOpp.candidate}
          calendarData={calendarData}
          onNoTimeAvailableFlowConfirm={async (
            confirmData,
            confirmedLegalTeamData
          ) => {
            if (confirmedLegalTeamData) {
              await handleLegalTeamDataUpdated(confirmedLegalTeamData);
            }
            await confirmInterview(confirmData);
          }}
          onConfirm={async (
            confirmData,
            scheduledEvent,
            confirmedLegalTeamData
          ) => {
            if (confirmedLegalTeamData) {
              await handleLegalTeamDataUpdated(confirmedLegalTeamData);
            }
            await scheduleInterview(confirmData, scheduledEvent);
          }}
          onClose={() => {
            setShowInterviewAuto(false);
            setTempEvent(undefined);
          }}
          primaryContactId={primaryContactId}
        />
      ) : null}
      {!!showEngageConfirmPrompt && (
        <EngagementPrompt
          candidateOpp={candidateOpp}
          onClose={handleEngageRequestToggle}
          onConfirm={handleEngageConfirm}
        />
      )}

      {displayLoginModal ? (
        <PublicEngagementToLoginModal
          onClose={() => {
            setDisplayLoginModal(false);
          }}
        />
      ) : null}
    </div>
  );
};
